import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "@/components/ui/sonner";
import { ArrowRight, Loader2, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const underline =
  "w-full bg-transparent border-0 border-b border-[#D4D4D4] rounded-none px-0 py-3 text-lg text-[#0A0A0A] placeholder:text-[#B4B4B4] focus:outline-none focus:border-[#3B37E5] transition-colors";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short note.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, service: "General inquiry" });
      setDone(true);
      toast.success("Thank you — we'll be in touch within 48 hours.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-start">
        <div className="lg:col-span-6">
          <p className="label-tech !text-[#3B37E5] mb-8">Start with a conversation</p>
          <h2 className="font-display text-[3rem] sm:text-6xl lg:text-7xl leading-[0.95] text-[#0A0A0A]">
            Ready for cleaner books and calmer tax preparation?
          </h2>
        </div>

        <div className="lg:col-span-6 lg:pt-3">
          {done ? (
            <motion.div
              data-testid="contact-success"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="border-t border-[#D4D4D4] pt-10"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="flex items-center justify-center h-9 w-9 rounded-full bg-[#3B37E5] text-white">
                  <Check size={18} />
                </span>
                <h3 className="font-display text-2xl text-[#0A0A0A]">Message received</h3>
              </div>
              <p className="text-[#52525B] max-w-md mb-8">
                A dedicated advisor will reach out within 48 hours. Keep an eye on your inbox.
              </p>
              <button
                data-testid="contact-reset"
                onClick={() => setDone(false)}
                className="label-tech !text-[#3B37E5] hover:!text-[#2A26D6] transition-colors"
              >
                Send another message
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="lead-form" className="space-y-9">
              <div>
                <label htmlFor="name" className="label-tech !text-[#0A0A0A] block mb-2">Name</label>
                <input id="name" data-testid="input-name" value={form.name} onChange={(e) => update("name", e.target.value)} className={underline} placeholder="Your name" />
              </div>
              <div>
                <label htmlFor="email" className="label-tech !text-[#0A0A0A] block mb-2">Email</label>
                <input id="email" type="email" data-testid="input-email" value={form.email} onChange={(e) => update("email", e.target.value)} className={underline} placeholder="you@company.com" />
              </div>
              <div>
                <label htmlFor="message" className="label-tech !text-[#0A0A0A] block mb-2">What needs attention?</label>
                <textarea id="message" data-testid="input-message" value={form.message} onChange={(e) => update("message", e.target.value)} rows={3} className={`${underline} resize-none`} placeholder="Tell us a little about your books or taxes..." />
              </div>
              <button
                type="submit"
                data-testid="submit-lead"
                disabled={loading}
                className="group inline-flex items-center gap-2 label-tech !text-[#3B37E5] hover:!text-[#2A26D6] transition-colors disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                Request a conversation
                {!loading && <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
